import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import AppointmentBooking from '../components/AppointmentBooking'

interface BrokerInfo {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string | null
  profile_picture_url: string | null
  profile_picture_settings: any
  title_en: string
  title_fr: string
}

interface FormConfiguration {
  form_title_en: string
  form_title_fr: string
  form_subtitle_en: string
  form_subtitle_fr: string
  submit_button_en: string
  submit_button_fr: string
  success_title_en: string
  success_title_fr: string
  success_message_en: string
  success_message_fr: string
  color_scheme: {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
    muted: string
  }
  custom_fields: any[]
  show_first_name: boolean
  show_last_name: boolean
  show_email: boolean
  show_phone: boolean
  show_message: boolean
  first_name_label_en: string
  first_name_label_fr: string
  last_name_label_en: string
  last_name_label_fr: string
  email_label_en: string
  email_label_fr: string
  phone_label_en: string
  phone_label_fr: string
  message_label_en: string
  message_label_fr: string
  message_placeholder_en: string
  message_placeholder_fr: string
  custom_css?: string
  // Priority fields
  priority_question_en: string
  priority_question_fr: string
  priority_urgent_en: string
  priority_urgent_fr: string
  priority_medium_en: string
  priority_medium_fr: string
  priority_low_en: string
  priority_low_fr: string
  // Contact time fields
  contact_time_question_en: string
  contact_time_question_fr: string
  contact_time_day_en: string
  contact_time_day_fr: string
  contact_time_evening_en: string
  contact_time_evening_fr: string
  contact_time_weekend_en: string
  contact_time_weekend_fr: string
}

interface FormData {
  first_name: string
  last_name: string
  email: string
  phone: string
  message: string
  priority: 'high' | 'medium' | 'low' | ''
  best_time_to_contact: 'day' | 'evening' | 'weekend' | ''
}

const CustomForm: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const [broker, setBroker] = useState<BrokerInfo | null>(null)
  const [formConfig, setFormConfig] = useState<FormConfiguration | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [contactMode, setContactMode] = useState<'immediate' | 'appointment' | null>(null)
  
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    priority: '',
    best_time_to_contact: ''
  })

  // Get texts based on language and configuration
  const getText = (enKey: keyof FormConfiguration, frKey: keyof FormConfiguration) => {
    if (!formConfig) return ''
    return i18n.language === 'fr' ? formConfig[frKey] as string : formConfig[enKey] as string
  }

  useEffect(() => {
    // Detect browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith('fr')) {
      i18n.changeLanguage('fr')
    } else {
      i18n.changeLanguage('en')
    }

    // Ensure dark mode is disabled for custom forms
    document.documentElement.classList.remove('dark')

    // Fetch broker info
    fetchBrokerInfo()
  }, [slug, i18n])

  const fetchBrokerInfo = async () => {
    if (!slug) return

    try {
      setLoading(true)
      const [brokerResponse, configResponse] = await Promise.all([
        makeApiCall(`/api/custom-forms/broker/${slug}`),
        makeApiCall(`/api/custom-forms/public-config/${slug}`)
      ])

      if (brokerResponse.ok) {
        const brokerData = await brokerResponse.json()
        setBroker(brokerData)
      } else {
        throw new Error('Failed to fetch broker info')
      }

      if (configResponse.ok) {
        const configData = await configResponse.json()
        setFormConfig(configData)
      }
    } catch (err: any) {
      console.error('Error fetching broker info:', err)
      if (err.message === 'Failed to fetch broker info') {
        setError('broker_not_found')
      } else {
        setError('fetch_error')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate at least one contact field
    if (!formData.first_name && !formData.last_name && !formData.email && !formData.phone) {
      alert(t('customForm.requiredField'))
      return
    }

    // Validate priority selection is required
    if (!formData.priority) {
      alert(t('customForm.selectPriority'))
      return
    }

    try {
      setSubmitting(true)
      await makeJsonApiCall(`/api/custom-forms/submit/${slug}`, 'POST', formData)
      setSubmitted(true)
    } catch (err: any) {
      console.error('Error submitting form:', err)
      setError('submit_error')
    } finally {
      setSubmitting(false)
    }
  }

  const formatPhoneNumber = (phone: string | null): string => {
    if (!phone) return ''
    // Basic phone formatting for display
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (error === 'broker_not_found' || !broker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('customForm.brokerNotFound')}</h1>
          <p className="text-gray-600">{t('customForm.brokerNotFoundMessage')}</p>
        </div>
      </div>
    )
  }

  // Generate CSS variables for dynamic colors
  const dynamicStyles = formConfig ? {
    '--primary-color': formConfig.color_scheme.primary,
    '--secondary-color': formConfig.color_scheme.secondary,
    '--accent-color': formConfig.color_scheme.accent,
    '--background-color': formConfig.color_scheme.background,
    '--text-color': formConfig.color_scheme.text,
    '--muted-color': formConfig.color_scheme.muted
  } as React.CSSProperties : {}

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-50 to-indigo-100"
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4 text-green-600">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{getText('success_title_en', 'success_title_fr')}</h1>
          <p className="text-gray-600 mb-6">{getText('success_message_en', 'success_message_fr')}</p>
          
          {/* Broker Contact Info */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-center mb-4">
              {broker.profile_picture_url ? (
                <img
                  src={broker.profile_picture_url}
                  alt={`${broker.first_name} ${broker.last_name}`}
                  className="w-16 h-16 rounded-full object-cover border-4"
                  style={{ borderColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20` }}
                />
              ) : (
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center border-4"
                  style={{ 
                    background: `linear-gradient(to right, ${formConfig?.color_scheme.primary || '#3B82F6'}, ${formConfig?.color_scheme.secondary || '#1E40AF'})`,
                    borderColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20`
                  }}
                >
                  <span className="text-xl font-bold text-white">
                    {broker.first_name.charAt(0)}{broker.last_name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <h3 className="font-medium text-gray-900">{broker.first_name} {broker.last_name}</h3>
            <p className="text-sm font-medium text-gray-600">
              {i18n.language === 'fr' ? broker.title_fr : broker.title_en}
            </p>
            <p className="text-sm text-gray-600 mt-2">{t('customForm.contactDirect')}</p>
            <div className="mt-2 space-y-1 text-sm">
              <a 
                href={`mailto:${broker.email}`}
                className="block hover:underline transition-all duration-200"
                style={{ color: formConfig?.color_scheme.primary || '#3B82F6' }}
              >
                {broker.email}
              </a>
              {broker.phone && (
                <a 
                  href={`tel:${broker.phone.replace(/\D/g, '')}`}
                  className="block hover:underline transition-all duration-200"
                  style={{ color: formConfig?.color_scheme.primary || '#3B82F6' }}
                >
                  {formatPhoneNumber(broker.phone)}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => i18n.changeLanguage('en')}
            className={`px-3 py-1 text-sm font-medium rounded-l-lg ${
              i18n.language === 'en' 
                ? 'text-white' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            style={i18n.language === 'en' ? {
              backgroundColor: formConfig?.color_scheme.primary || '#3B82F6'
            } : {}}
          >
            EN
          </button>
          <button
            onClick={() => i18n.changeLanguage('fr')}
            className={`px-3 py-1 text-sm font-medium rounded-r-lg ${
              i18n.language === 'fr' 
                ? 'text-white' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            style={i18n.language === 'fr' ? {
              backgroundColor: formConfig?.color_scheme.primary || '#3B82F6'
            } : {}}
          >
            FR
          </button>
        </div>
      </div>

      <div className="flex items-center justify-center min-h-screen px-4 py-12">
        <div className={`w-full bg-white rounded-lg shadow-xl transition-all duration-500 ${
          contactMode ? 'max-w-6xl h-[calc(100vh-6rem)]' : 'max-w-lg p-8'
        }`}>
          {!contactMode ? (
            // COMPACT VIEW: No option selected yet
            <>
              {/* Broker Header */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  {broker.profile_picture_url ? (
                    <img
                      src={broker.profile_picture_url}
                      alt={`${broker.first_name} ${broker.last_name}`}
                      className="w-20 h-20 rounded-full object-cover border-4 border-blue-100"
                    />
                  ) : (
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center border-4"
                      style={{ 
                        background: `linear-gradient(to right, ${formConfig?.color_scheme.primary || '#3B82F6'}, ${formConfig?.color_scheme.secondary || '#1E40AF'})`,
                        borderColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20`
                      }}
                    >
                      <span className="text-2xl font-bold text-white">
                        {broker.first_name.charAt(0)}{broker.last_name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{broker.first_name} {broker.last_name}</h1>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.language === 'fr' ? broker.title_fr : broker.title_en}
                </p>
                <a 
                  href={`mailto:${broker.email}`}
                  className="text-gray-600 hover:text-gray-900 hover:underline transition-all duration-200 block mb-1"
                >
                  {broker.email}
                </a>
                {broker.phone && (
                  <a 
                    href={`tel:${broker.phone.replace(/\D/g, '')}`}
                    className="text-gray-600 hover:text-gray-900 hover:underline transition-all duration-200 block"
                  >
                    {formatPhoneNumber(broker.phone)}
                  </a>
                )}
              </div>

              {/* Form Header */}
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-2">{getText('form_title_en', 'form_title_fr')}</h2>
                <p className="text-gray-600">{getText('form_subtitle_en', 'form_subtitle_fr')}</p>
              </div>

              {/* Contact Mode Selection */}
              <div className="mb-0">
                <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                  {t('customForm.howToBeContacted')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setContactMode('immediate')}
                    className="p-6 rounded-lg border-2 transition-all duration-200 text-left bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                  >
                    <div className="flex items-start space-x-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center mt-1"
                        style={{ backgroundColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20` }}
                      >
                        <svg 
                          className="w-4 h-4" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          style={{ color: formConfig?.color_scheme.primary || '#3B82F6' }}
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">
                          {t('customForm.contactMeASAP')}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {t('customForm.contactMeASAPDesc')}
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setContactMode('appointment')}
                    className="p-6 rounded-lg border-2 transition-all duration-200 text-left bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                  >
                    <div className="flex items-start space-x-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center mt-1"
                        style={{ backgroundColor: `${formConfig?.color_scheme.secondary || '#10B981'}20` }}
                      >
                        <svg 
                          className="w-4 h-4" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          style={{ color: formConfig?.color_scheme.secondary || '#10B981' }}
                        >
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">
                          {t('customForm.scheduleAppointment')}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {t('customForm.scheduleAppointmentDesc')}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </>
          ) : (
            // EXPANDED VIEW: Two-column layout
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 h-full overflow-hidden">
              {/* LEFT SIDEBAR: Broker Info */}
              <div className="lg:col-span-4 p-8 lg:border-r border-gray-200 overflow-y-auto">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    {broker.profile_picture_url ? (
                      <img
                        src={broker.profile_picture_url}
                        alt={`${broker.first_name} ${broker.last_name}`}
                        className="w-20 h-20 rounded-full object-cover border-4 border-blue-100"
                      />
                    ) : (
                      <div 
                        className="w-20 h-20 rounded-full flex items-center justify-center border-4"
                        style={{ 
                          background: `linear-gradient(to right, ${formConfig?.color_scheme.primary || '#3B82F6'}, ${formConfig?.color_scheme.secondary || '#1E40AF'})`,
                          borderColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20`
                        }}
                      >
                        <span className="text-2xl font-bold text-white">
                          {broker.first_name.charAt(0)}{broker.last_name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <h1 className="text-xl font-bold text-gray-900 mb-1">{broker.first_name} {broker.last_name}</h1>
                  <p className="text-sm font-medium text-gray-600 mb-3">
                    {i18n.language === 'fr' ? broker.title_fr : broker.title_en}
                  </p>
                  <a 
                    href={`mailto:${broker.email}`}
                    className="text-sm text-gray-600 hover:text-gray-900 hover:underline transition-all duration-200 block mb-1"
                  >
                    {broker.email}
                  </a>
                  {broker.phone && (
                    <a 
                      href={`tel:${broker.phone.replace(/\D/g, '')}`}
                      className="text-sm text-gray-600 hover:text-gray-900 hover:underline transition-all duration-200 block"
                    >
                      {formatPhoneNumber(broker.phone)}
                    </a>
                  )}
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setContactMode('immediate')}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left bg-white ${
                      contactMode === 'immediate'
                        ? 'border-2 text-gray-800 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                    style={contactMode === 'immediate' ? {
                      borderColor: formConfig?.color_scheme.primary || '#3B82F6'
                    } : {}}
                  >
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${formConfig?.color_scheme.primary || '#3B82F6'}20` }}
                      >
                        <svg 
                          className="w-3 h-3" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          style={{ color: formConfig?.color_scheme.primary || '#3B82F6' }}
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </div>
                      <span className="font-medium text-sm">
                        {t('customForm.contactMeASAP')}
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => setContactMode('appointment')}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left bg-white ${
                      contactMode === 'appointment'
                        ? 'border-2 text-gray-800 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                    style={contactMode === 'appointment' ? {
                      borderColor: formConfig?.color_scheme.primary || '#3B82F6'
                    } : {}}
                  >
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${formConfig?.color_scheme.secondary || '#10B981'}20` }}
                      >
                        <svg 
                          className="w-3 h-3" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          style={{ color: formConfig?.color_scheme.secondary || '#10B981' }}
                        >
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="font-medium text-sm">
                        {t('customForm.scheduleAppointment')}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* RIGHT CONTENT AREA */}
              <div className="lg:col-span-8 p-8 overflow-y-auto">
                {contactMode === 'appointment' ? (
                  <AppointmentBooking 
                    brokerSlug={slug || ''}
                    onBookingComplete={() => {
                      setSubmitted(true)
                    }}
                    className="mb-4"
                  />
                ) : contactMode === 'immediate' ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formConfig?.show_first_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getText('first_name_label_en', 'first_name_label_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2"
                    style={{ 
                      '--focus-color': formConfig?.color_scheme.primary || '#3B82F6'
                    } as React.CSSProperties}
                    onFocus={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = formConfig?.color_scheme.primary || '#3B82F6'
                      ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}20`
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = '#D1D5DB'
                      ;(e.target as HTMLInputElement).style.boxShadow = 'none'
                    }}
                  />
                </div>
              )}
              {formConfig?.show_last_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getText('last_name_label_en', 'last_name_label_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2"
                    style={{ 
                      '--focus-color': formConfig?.color_scheme.primary || '#3B82F6'
                    } as React.CSSProperties}
                    onFocus={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = formConfig?.color_scheme.primary || '#3B82F6'
                      ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}20`
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = '#D1D5DB'
                      ;(e.target as HTMLInputElement).style.boxShadow = 'none'
                    }}
                  />
                </div>
              )}
            </div>

            {formConfig?.show_email && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {getText('email_label_en', 'email_label_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2"
                  style={{ 
                    '--focus-color': formConfig?.color_scheme.primary || '#3B82F6'
                  } as React.CSSProperties}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = formConfig?.color_scheme.primary || '#3B82F6'
                    ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}20`
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = '#D1D5DB'
                    ;(e.target as HTMLInputElement).style.boxShadow = 'none'
                  }}
                />
              </div>
            )}

            {formConfig?.show_phone && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {getText('phone_label_en', 'phone_label_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2"
                  style={{ 
                    '--focus-color': formConfig?.color_scheme.primary || '#3B82F6'
                  } as React.CSSProperties}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = formConfig?.color_scheme.primary || '#3B82F6'
                    ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}20`
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = '#D1D5DB'
                    ;(e.target as HTMLInputElement).style.boxShadow = 'none'
                  }}
                />
              </div>
            )}

            {formConfig?.show_message && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {getText('message_label_en', 'message_label_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
                </label>
                <textarea
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={getText('message_placeholder_en', 'message_placeholder_fr')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2"
                  style={{ 
                    '--focus-color': formConfig?.color_scheme.primary || '#3B82F6'
                  } as React.CSSProperties}
                  onFocus={(e) => {
                    (e.target as HTMLTextAreaElement).style.borderColor = formConfig?.color_scheme.primary || '#3B82F6'
                    ;(e.target as HTMLTextAreaElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}20`
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLTextAreaElement).style.borderColor = '#D1D5DB'
                    ;(e.target as HTMLTextAreaElement).style.boxShadow = 'none'
                  }}
                />
              </div>
            )}

            {/* Priority Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {getText('priority_question_en', 'priority_question_fr')}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 'high', labelEn: 'priority_urgent_en', labelFr: 'priority_urgent_fr' },
                  { value: 'medium', labelEn: 'priority_medium_en', labelFr: 'priority_medium_fr' },
                  { value: 'low', labelEn: 'priority_low_en', labelFr: 'priority_low_fr' }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, priority: option.value as 'high' | 'medium' | 'low' }))}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium bg-white ${
                      formData.priority === option.value
                        ? 'text-gray-800'
                        : 'text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                    style={formData.priority === option.value ? {
                      borderColor: formConfig?.color_scheme.primary || '#3B82F6'
                    } : {}}
                  >
                    {getText(option.labelEn as keyof FormConfiguration, option.labelFr as keyof FormConfiguration)}
                  </button>
                ))}
              </div>
            </div>

            {/* Best Time to Contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {getText('contact_time_question_en', 'contact_time_question_fr')} <span className="text-gray-400 text-xs">{t('customForm.optional')}</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 'day', labelEn: 'contact_time_day_en', labelFr: 'contact_time_day_fr' },
                  { value: 'evening', labelEn: 'contact_time_evening_en', labelFr: 'contact_time_evening_fr' },
                  { value: 'weekend', labelEn: 'contact_time_weekend_en', labelFr: 'contact_time_weekend_fr' }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      best_time_to_contact: prev.best_time_to_contact === option.value ? '' : option.value as 'day' | 'evening' | 'weekend'
                    }))}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium bg-white ${
                      formData.best_time_to_contact === option.value
                        ? 'text-gray-800'
                        : 'text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                    style={formData.best_time_to_contact === option.value ? {
                      borderColor: formConfig?.color_scheme.primary || '#3B82F6'
                    } : {}}
                  >
                    {getText(option.labelEn as keyof FormConfiguration, option.labelFr as keyof FormConfiguration)}
                  </button>
                ))}
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500 mb-4">{t('customForm.atLeastOneField')}</p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full text-white py-3 px-6 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                style={{
                  backgroundColor: formConfig?.color_scheme.primary || '#3B82F6'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    const primaryColor = formConfig?.color_scheme.primary || '#3B82F6'
                    // Darken the color by 10% for hover
                    const darkerColor = primaryColor.replace('#', '').match(/.{1,2}/g)?.map((hex: string) => {
                      const num = parseInt(hex, 16)
                      const darker = Math.max(0, num - 25)
                      return darker.toString(16).padStart(2, '0')
                    }).join('') || primaryColor.replace('#', '')
                    ;(e.target as HTMLButtonElement).style.backgroundColor = `#${darkerColor}`
                  }
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = formConfig?.color_scheme.primary || '#3B82F6'
                }}
                onFocus={(e) => {
                  ;(e.target as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${formConfig?.color_scheme.primary || '#3B82F6'}40`
                }}
                onBlur={(e) => {
                  ;(e.target as HTMLButtonElement).style.boxShadow = ''
                }}
              >
                {submitting 
                  ? t('customForm.submitting') 
                  : getText('submit_button_en', 'submit_button_fr')
                }
              </button>
            </div>
          </form>
                ) : null}

                {error === 'submit_error' && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">{t('customForm.errorTitle')}</h3>
                        <p className="text-sm text-red-700 mt-1">{t('customForm.errorMessage')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="pb-8 text-center">
        <p className="text-xs text-gray-500">
          {t('customForm.poweredBy')}{' '}
          <a 
            href="https://mortgageflow.pro" 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-medium text-gray-900 hover:text-blue-700 hover:underline transition-colors"
          >
            MortgageFlow
          </a>
        </p>
      </div>
    </div>
  )
}

export default CustomForm